import React, { useEffect, useState } from "react";
import { Gift, Sparkles, Twitter, Facebook, Copy, Check, Mail } from "lucide-react";

/**
 * Celebration banner shown on the order-success page when the customer
 * has unlocked a £25 thank-you gift card by spending over £300.
 *
 * Unlike the Brother-bundle picker (which lets the customer choose on-
 * site), the spend-tier reward is claimed via email reply so the admin
 * can manually verify and dispatch. We surface the same instructions
 * here so the customer sees the win immediately, and offer share-with-
 * a-friend buttons to amplify reach.
 *
 * Props:
 *   value: number   (gift card amount, default £25)
 *   orderId: string (used to ensure unique key)
 *   customerEmail: string  (shown so they know which inbox to check)
 */
export default function SpendTierGiftCardCelebration({ value = 25, orderId = "", customerEmail = "" }) {
  const [copied, setCopied] = useState(false);
  const [confetti, setConfetti] = useState(true);

  // Auto-stop the simple confetti animation after a few seconds for performance
  useEffect(() => {
    const t = setTimeout(() => setConfetti(false), 5000);
    return () => clearTimeout(t);
  }, []);

  const shareText = `I just unlocked a £${value} thank-you gift card from Carepoint Health Supplies! 🎁 Their range of medical, dental & care supplies is fantastic.`;
  const shareUrl = "https://carepointuk.net";

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-6 md:p-8 mb-6 text-white shadow-xl"
      style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 60%, #b45309 100%)" }}
      data-testid="spend-tier-celebration"
    >
      {/* Decorative confetti dots */}
      {confetti && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {[...Array(28)].map((_, i) => {
            const left = (i * 37) % 100;
            const delay = (i % 12) * 0.18;
            const size = 4 + (i % 5) * 2;
            const colours = ["#fff", "#fde68a", "#fef3c7", "#fbbf24"];
            const c = colours[i % colours.length];
            return (
              <span
                key={i}
                className="absolute rounded-full opacity-80"
                style={{
                  left: `${left}%`,
                  top: "-10px",
                  width: `${size}px`,
                  height: `${size}px`,
                  background: c,
                  animation: `confettiFall 4.2s ${delay}s ease-in forwards`,
                }}
              />
            );
          })}
        </div>
      )}

      <div className="absolute -top-6 -right-6 text-white/10">
        <Gift className="w-44 h-44" />
      </div>

      <div className="relative">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-3">
          <Sparkles className="w-3.5 h-3.5" />
          Unlocked!
        </div>
        <h2 className="text-3xl md:text-4xl font-extrabold leading-tight" data-testid="celebration-headline">
          You&apos;ve earned a FREE &pound;{Number(value).toFixed(0)} Gift Card 🎁
        </h2>
        <p className="mt-2 text-white/90 max-w-xl">
          Thank you for spending over &pound;300 with us. Choose your favourite high-street retailer
          and we&apos;ll post your gift card within 3 working days.
        </p>

        <div className="mt-5 bg-white/15 backdrop-blur rounded-xl p-4 border border-white/20">
          <div className="flex items-center gap-2 text-sm font-bold mb-2">
            <Mail className="w-4 h-4" /> How to claim
          </div>
          <p className="text-sm text-white/90 mb-2">
            We&apos;ve just emailed your order confirmation
            {customerEmail ? <> to <strong>{customerEmail}</strong></> : null}.
            <strong> Reply to that email</strong> with:
          </p>
          <ul className="text-sm space-y-1 list-disc list-inside text-white/95">
            <li>
              Your preferred gift card brand —{" "}
              <span className="font-semibold">e.g. TESCO</span>,{" "}
              Amazon, John Lewis, M&amp;S, Costa, Argos, Boots
            </li>
            <li>Your full name</li>
            <li>Delivery address &amp; department / ward</li>
          </ul>
          <p className="text-xs text-white/80 mt-3">
            Can&apos;t see the email? Check your spam folder, or contact
            <a href="mailto:orders@carepointuk.net" className="underline ml-1">orders@carepointuk.net</a>.
          </p>
        </div>

        {/* Share buttons */}
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="share-row">
          <span className="text-sm font-semibold mr-1">Share your win:</span>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-black/30 hover:bg-black/50 text-white text-sm px-3 py-1.5 rounded-md transition"
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4" /> Tweet
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-black/30 hover:bg-black/50 text-white text-sm px-3 py-1.5 rounded-md transition"
            data-testid="share-facebook"
          >
            <Facebook className="w-4 h-4" /> Share
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent("Have you tried Carepoint Health Supplies?")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
            className="inline-flex items-center gap-1.5 bg-black/30 hover:bg-black/50 text-white text-sm px-3 py-1.5 rounded-md transition"
            data-testid="share-email"
          >
            <Mail className="w-4 h-4" /> Email a friend
          </a>
          <button
            type="button"
            onClick={copyShareLink}
            className="inline-flex items-center gap-1.5 bg-black/30 hover:bg-black/50 text-white text-sm px-3 py-1.5 rounded-md transition"
            data-testid="share-copy"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) rotate(0deg);  opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(280px) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
